export type ProductCategory = 'toc' | 'tob';
export type ProductStatus = 'live' | 'beta' | 'coming_soon' | 'restructuring';

export interface ProductStat {
  label: string;
  value: string;
}

export interface ProductFeature {
  title: string;
  description: string;
  highlights: string[];
}

export interface Product {
  id: string;
  name: string;
  displayName?: string;
  tagline: string;
  description: string;
  category: ProductCategory;
  status: ProductStatus;
  platform?: string;
  icon: string;
  cardVisual?: string;
  iconEmoji?: string;
  route?: string;
  externalLink?: string;
  brandColor: string;
  brandColorSoft: string;
  heroGradient: string;
  mascot?: string;
  screenshots?: string[];
  stats?: ProductStat[];
  bgGradient?: string;
  borderColor?: string;
  features?: ProductFeature[];
  scenarios?: string[];
  ctaText?: string;
  ctaLink?: string;
  ctaExternal?: boolean;
}

export const STATUS_LABELS: Record<ProductStatus, string> = {
  live: '已上线',
  beta: '内测中',
  coming_soon: '即将推出',
  restructuring: '升级重构中',
};

export const CATEGORY_LABELS: Record<ProductCategory, string> = {
  toc: '消费者产品',
  tob: '企业产品',
};

export const products: Product[] = [
  {
    id: 'ququ',
    name: 'QuQu',
    displayName: '蛐蛐 QuQu',
    tagline: '你的私人关系管家',
    description:
      '记住每个人，记录每件事。QuQu 帮助您长期记录、维护现实人际关系——人物档案、互动记录、关系图谱与智能提醒，让社交维护从「凭记忆」变成「可管理、可复盘」。',
    category: 'toc',
    status: 'live',
    platform: 'iOS / Android',
    icon: '/images/products/ququ/icon-1024.png',
    cardVisual: '/images/products/ququ/card-visual.png',
    mascot: '/images/products/ququ/mascot.png',
    route: '/products/ququ',
    brandColor: '#4ECDC4',
    brandColorSoft: '#E8FAF8',
    heroGradient: 'linear-gradient(135deg, #4ECDC4 0%, #2DD4BF 50%, #14B8A6 100%)',
    screenshots: [
      '/images/products/ququ/screenshots/home.png',
      '/images/products/ququ/screenshots/people.png',
      '/images/products/ququ/screenshots/graph.png',
      '/images/products/ququ/screenshots/detail.png',
      '/images/products/ququ/screenshots/profile.png',
    ],
    stats: [
      { label: '定位', value: '关系管家' },
      { label: '平台', value: 'iOS/Android' },
      { label: '核心', value: '人物+图谱' },
      { label: '会员', value: 'QuQu Pro' },
    ],
    bgGradient: 'from-teal-50 to-emerald-50',
    borderColor: 'border-teal-200',
    features: [
      {
        title: '人物档案',
        description: 'AI 文本录入或手动建档，标签、圈子与关系类型一目了然。',
        highlights: ['AI 录入', '标签分组', '关系类型'],
      },
      {
        title: '互动记录',
        description: '见面、通话、送礼、聚餐等 9 类互动，完整留存每一次联系。',
        highlights: ['多类型记录', '时间线', '亲密度'],
      },
      {
        title: '关系图谱',
        description: '可视化关系网络，直观看见人际连接与亲密度变化。',
        highlights: ['图谱视图', '亲密度', '勋章体系'],
      },
      {
        title: '智能提醒',
        description: '生日、纪念日、定期联系等待办，不再错过重要的人。',
        highlights: ['生日提醒', '定期联系', 'iOS 小组件'],
      },
    ],
    scenarios: [
      '需要系统维护亲友、同事与客户关系的职场人',
      '销售、创业者等需长期经营关系网络的人群',
      '重视隐私、希望关系可复盘管理的用户',
    ],
    ctaText: '申请体验',
    ctaLink: '/contact',
    ctaExternal: false,
  },
  {
    id: 'salary-reward',
    name: '薪资奖励官',
    displayName: '薪资奖励官',
    tagline: '抬腕看今天赚了多少钱',
    description:
      'Apple Watch 优先的实时计薪陪伴 App。不算复杂账本，只让你看见每一分钟的价值；小福狸陪你把工资攒成愿望，让工作更有成就感。',
    category: 'toc',
    status: 'live',
    platform: 'Apple Watch + iOS',
    icon: '/images/products/salary-reward/icon-1024.png',
    cardVisual: '/images/products/salary-reward/card-visual.png',
    mascot: '/images/products/salary-reward/mascot.png',
    route: '/products/salary-reward',
    externalLink: 'https://apps.apple.com',
    brandColor: '#FF9F43',
    brandColorSoft: '#FFF4E8',
    heroGradient: 'linear-gradient(135deg, #FF9F43 0%, #E8750A 50%, #D4620A 100%)',
    stats: [
      { label: '核心', value: '抬腕计薪' },
      { label: '伙伴', value: '小福狸' },
      { label: '更新', value: '每10秒' },
      { label: '平台', value: 'watchOS' },
    ],
    bgGradient: 'from-orange-50 to-amber-50',
    borderColor: 'border-orange-200',
    features: [
      {
        title: '表盘实时计薪',
        description: '复杂功能展示今日已赚、日进度与计薪开关，抬腕即见。',
        highlights: ['表盘组件', '实时更新', '进度环'],
      },
      {
        title: '愿望目标',
        description: '奶茶、旅行、数码等模板，日薪自动累积到目标进度。',
        highlights: ['多目标', '进度可视化', '成就庆祝'],
      },
      {
        title: '小福狸陪伴',
        description: '像素风虚拟伙伴，工作/鼓励/加班/休息等状态切换与暖心气泡。',
        highlights: ['角色动画', '情景对话', '情绪价值'],
      },
      {
        title: '隐私优先',
        description: '数据存本机 + 可选 iCloud，不上传开发者服务器。',
        highlights: ['本地存储', 'iCloud 同步', '无账号体系'],
      },
    ],
    scenarios: [
      '佩戴 Apple Watch 的职场打工人',
      '希望用可视化进度激励自己的固定/弹性工时用户',
      '追求情绪价值而非复杂记账的轻量用户',
    ],
    ctaText: 'App Store 下载',
    ctaLink: 'https://apps.apple.com',
    ctaExternal: true,
  },
  {
    id: 'dingwuyou',
    name: '订无忧',
    displayName: '订无忧',
    tagline: '看清订阅，控制支出',
    description:
      '原生 iOS 订阅管理应用，围绕「看清订阅、控制支出、及时提醒」构建：订阅全生命周期管理、按时间与分类的消费分析、到期通知、云同步与完整备份恢复。月度与年度成本由同一套换算引擎输出，使用率只来自你自己的记录。',
    category: 'toc',
    status: 'restructuring',
    // 真实工程为 SwiftUI 原生 iOS（iOS 17.0+），此前标注的 Android 并不存在
    platform: 'iOS 17+',
    icon: '/images/products/dingwuyou/icon-1024.png',
    cardVisual: '/images/products/dingwuyou/card-visual.png',
    route: '/products/dingwuyou',
    // 品牌色取自 App 内实际主色（进度条、图表、分类标签），此前的红色与产品不符
    brandColor: '#7C5CE6',
    brandColorSoft: '#F1EDFE',
    heroGradient: 'linear-gradient(135deg, #7C5CE6 0%, #6D48D7 50%, #5B34C4 100%)',
    stats: [
      { label: '平台', value: 'iOS 17+' },
      { label: '成本口径', value: '月/年统一' },
      { label: '数据', value: '本地优先' },
      { label: '状态', value: '升级重构中' },
    ],
    bgGradient: 'from-violet-50 to-indigo-50',
    borderColor: 'border-violet-200',
    // 取自 App 真实截图（1206×2622，iPhone 16 Pro）。
    // 刻意避开内容重复的几张：subscription_list 与 home 是同一个界面。
    screenshots: [
      '/images/screenshots/home.png',
      '/images/screenshots/daoqitixing.png',
      '/images/screenshots/cost_analysis.png',
      '/images/screenshots/subscription_detail.png',
    ],
    features: [
      {
        title: '订阅全生命周期',
        description:
          '金额、周期、分类、起止日期与提醒一次录入。到期订阅经你确认后进入历史，可恢复也可永久删除。',
        highlights: ['周期换算', '分类归集', '历史可恢复'],
      },
      {
        title: '统一的成本口径',
        description:
          '月度与年度金额由同一个换算引擎输出，各个页面不自己算一套，看到的数字始终对得上。',
        highlights: ['月度视图', '年度视图', '口径一致'],
      },
      {
        title: '到期提醒',
        description:
          '到期前推送通知，首页同时给出即将到期的数量。提醒设置有独立快照，配置损坏时自动回退。',
        highlights: ['到期通知', '风险前置', '设置可回退'],
      },
      {
        title: '消费分析',
        description:
          '按周/月/季/年看支出走势，按分类看钱花在哪。优化建议基于你的真实订阅与使用记录生成。',
        highlights: ['时间维度', '分类占比', '真实数据'],
      },
      {
        title: '同步与备份',
        description:
          '是否联网同步由你的网络偏好决定，并可导出 JSON 完整备份。导入前校验版本、ID 唯一性与金额。',
        highlights: ['按网络同步', 'JSON 备份', '导入校验'],
      },
      {
        title: '本地数据安全',
        description:
          '订阅数据以原子快照落盘并维护可恢复备份，主文件损坏自动启用备份；登录凭据存于 Keychain。',
        highlights: ['原子写入', '损坏自愈', 'Keychain'],
      },
    ],
    scenarios: [
      '订阅项目多、记不清哪个月该扣哪一笔的个人用户',
      '想弄清钱花在哪类服务上、决定续订还是取消的人',
      '不愿意让订阅数据只存在云端、需要本地备份的用户',
    ],
    ctaText: '了解产品进展',
    ctaLink: '/contact',
    ctaExternal: false,
  },
  {
    id: 'sales-agent',
    name: '销售智能体',
    displayName: '销售智能体',
    tagline: '公域到私域，一站式增长',
    description:
      '面向本地中小企业的 AI 销售增长系统：选题口播、合规审核、抖音/视频号发布、评论私信接线索、微信私域承接与 ROI 复盘——让人少的情况下也能持续曝光、接线索、算回报。',
    category: 'tob',
    status: 'beta',
    platform: 'SaaS + Edge Agent',
    icon: '/images/products/sales-agent/icon-1024.png',
    cardVisual: '/images/products/sales-agent/card-visual.png',
    route: '/products/sales-agent',
    brandColor: '#1677FF',
    brandColorSoft: '#E8F3FF',
    heroGradient: 'linear-gradient(135deg, #1677FF 0%, #0958D9 50%, #003EB3 100%)',
    stats: [
      { label: '闭环', value: '公域→私域' },
      { label: '形态', value: 'SaaS' },
      { label: '行业', value: '6+ 行业包' },
      { label: '状态', value: '内测' },
    ],
    bgGradient: 'from-blue-50 to-indigo-50',
    borderColor: 'border-blue-200',
    features: [
      {
        title: '内容生产',
        description: '选题 → 口播脚本 → 合规审核 → 视频合成，降低内容产出门槛。',
        highlights: ['行业话术', '合规审核', '自动成片'],
      },
      {
        title: '多渠道发布',
        description: '抖音真机 RPA 已接，视频号/小红书持续扩展，稳定日更隔日更。',
        highlights: ['抖音发布', '视频号', 'Edge Agent'],
      },
      {
        title: '私域接待',
        description: '评论/私信进线 → 接待建议 → 引导加微，高风险外发可人工确认。',
        highlights: ['线索捕获', '话术建议', '人工审核'],
      },
      {
        title: '老板看板',
        description: '经营摘要、待拍板事项、漏斗与 ROI 复盘，数据驱动决策。',
        highlights: ['ROI 台账', '漏斗视图', '企微告警'],
      },
    ],
    scenarios: [
      '教培、美业、餐饮、本地服务等本地中小企业主',
      '希望用短视频持续获客但缺乏内容团队的品牌',
      '需要把公域曝光与私域转化串成闭环的老板',
    ],
    ctaText: '预约演示',
    ctaLink: '/contact',
    ctaExternal: false,
  },
  {
    id: 'ranking-miniprogram',
    name: '排名小程序',
    displayName: '排名小程序',
    tagline: '机构积分排名与激励工具',
    description:
      '基于微信小程序的团队积分排名与激励 SaaS（内部产品名「谁是黑马」）。管理员维护人员库、积分与排名，成员查看榜单、积分兑奖，形成竞赛感与线下激励闭环。',
    category: 'tob',
    status: 'live',
    platform: '微信小程序',
    icon: '/images/products/ranking/icon-1024.png',
    cardVisual: '/images/products/ranking/card-visual.png',
    mascot: '/images/products/ranking/crown.png',
    route: '/products/ranking-miniprogram',
    brandColor: '#F59E0B',
    brandColorSoft: '#FFF8EB',
    heroGradient: 'linear-gradient(135deg, #F59E0B 0%, #EA580C 50%, #DC2626 100%)',
    stats: [
      { label: '平台', value: '微信小程序' },
      { label: '模式', value: 'SaaS 订阅' },
      { label: '榜单', value: '个人+队伍' },
      { label: '激励', value: '积分兑奖' },
    ],
    bgGradient: 'from-amber-50 to-orange-50',
    borderColor: 'border-amber-200',
    features: [
      {
        title: '个人榜与队伍榜',
        description: '按后台录入的排名展示，支持省市区、年级筛选与跨机构聚合。',
        highlights: ['实时榜单', '队伍 PK', '多维筛选'],
      },
      {
        title: '积分体系',
        description: '管理员设置与调整积分，兑换扣积分不改变排名，正式用户可看流水。',
        highlights: ['积分管理', '兑换记录', '权限分层'],
      },
      {
        title: '奖品兑换',
        description: '积分换礼、库存管理、线下领取确认，激励落地可追踪。',
        highlights: ['奖品库', '库存管理', '领取确认'],
      },
      {
        title: '机构后台',
        description: '人员/队伍/奖品/兑换/Banner 多租户管理，按订阅规模灵活扩展。',
        highlights: ['多租户', '管理后台', 'Banner 运营'],
      },
    ],
    scenarios: [
      '培训机构、课程机构的学员积分与排名激励',
      '有明确 KPI 的内部销售、运营、渠道团队',
      '希望通过 gamification 透明化绩效的中小企业',
    ],
    ctaText: '了解合作',
    ctaLink: '/contact',
    ctaExternal: false,
  },
];

export const tocProducts = products.filter((p) => p.category === 'toc');
export const tobProducts = products.filter((p) => p.category === 'tob');

export function getProductById(id: string): Product | undefined {
  return products.find((p) => p.id === id);
}

export function getProductDisplayName(product: Product): string {
  return product.displayName ?? product.name;
}

export function getProductsWithRoute(): Product[] {
  return products.filter((p) => p.route);
}
